import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  GitPullRequest,
  Search,
  Filter,
  Sparkles,
  Check,
  X,
  Eye,
  Clock,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  ai_reviewed: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  approved: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  partially_approved: 'bg-violet-500/10 text-violet-400 border border-violet-500/20',
  rejected: 'bg-rose-500/10 text-rose-400 border border-rose-500/20',
};

export default function ChangeRequests() {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [msaDocuments, setMsaDocuments] = useState([]);
  const [poDocuments, setPoDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);

  const [form, setForm] = useState({
    document_id: '',
    document_type: 'msa',
    requested_change: '',
    section: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, msaRes, poRes] = await Promise.all([
        api().get('/change-requests'),
        api().get('/msa/documents'),
        api().get('/po/documents'),
      ]);
      setRequests(requestsRes.data);
      setMsaDocuments(msaRes.data);
      setPoDocuments(poRes.data);
    } catch (error) {
      toast.error('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api().post('/change-requests', form);
      toast.success('Change request submitted');
      setCreateModalOpen(false);
      setForm({ document_id: '', document_type: 'msa', requested_change: '', section: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create change request');
    }
  };

  const handleAIReview = async (requestId) => {
    setAiReviewLoading(true);
    try {
      const response = await api().post(`/change-requests/${requestId}/ai-review`);
      toast.success('AI review completed');
      setSelectedRequest({
        ...selectedRequest,
        ai_suggestion: response.data.suggestion,
        ai_recommendation: response.data.recommendation,
        status: 'ai_reviewed',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI review failed');
    } finally {
      setAiReviewLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api().post(`/change-requests/${requestId}/approve`);
      toast.success('Change request approved');
      setViewModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await api().post(`/change-requests/${requestId}/reject`);
      toast.success('Change request rejected');
      setViewModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const filteredRequests = requests.filter((r) => {
    const matchesSearch = r.requested_change.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const canApprove = user?.role === 'admin' || user?.role === 'approver';
  const documents = form.document_type === 'msa' ? msaDocuments : poDocuments;

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="change-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
            Change Requests
          </h1>
          <p className="text-zinc-400 mt-1">
            Review and manage document change requests with AI assistance
          </p>
        </div>
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]"
              data-testid="new-change-request-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Change Request
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Submit Change Request</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Request a change to an existing document
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Document Type</Label>
                <Select
                  value={form.document_type}
                  onValueChange={(v) =>
                    setForm({ ...form, document_type: v, document_id: '' })
                  }
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="msa">MSA Document</SelectItem>
                    <SelectItem value="po">Purchase Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Document</Label>
                <Select
                  value={form.document_id}
                  onValueChange={(v) => setForm({ ...form, document_id: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue placeholder="Select document" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {documents.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.vendor_name || doc.candidate_name} - {doc.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Section (Optional)</Label>
                <Input
                  value={form.section}
                  onChange={(e) => setForm({ ...form, section: e.target.value })}
                  placeholder="e.g., Payment Terms, Liability Clause"
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Requested Change</Label>
                <Textarea
                  value={form.requested_change}
                  onChange={(e) => setForm({ ...form, requested_change: e.target.value })}
                  placeholder="Describe the change you're requesting..."
                  className="bg-zinc-950 border-zinc-800 min-h-[120px]"
                  required
                  data-testid="requested-change-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateModalOpen(false)}
                  className="border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-500"
                  disabled={!form.document_id || !form.requested_change}
                  data-testid="submit-change-request-btn"
                >
                  Submit Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search change requests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="ai_reviewed">AI Reviewed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Change Requests List */}
      <div className="space-y-4">
        {filteredRequests.length > 0 ? (
          filteredRequests.map((request) => (
            <Card
              key={request.id}
              className="bg-zinc-900/40 border-zinc-800 card-hover cursor-pointer"
              onClick={() => {
                setSelectedRequest(request);
                setViewModalOpen(true);
              }}
              data-testid={`change-request-${request.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={statusColors[request.status]}>
                        {request.status.replace('_', ' ')}
                      </Badge>
                      <span className="text-xs text-zinc-500 uppercase">
                        {request.document_type}
                      </span>
                      {request.section && (
                        <span className="text-xs text-zinc-400">
                          Section: {request.section}
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-200 line-clamp-2">
                      {request.requested_change}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(request.created_at).toLocaleDateString()}
                      </span>
                      {request.ai_recommendation && (
                        <span className="flex items-center gap-1 text-blue-400">
                          <Bot className="w-3 h-3" />
                          AI: {request.ai_recommendation}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-zinc-400 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRequest(request);
                      setViewModalOpen(true);
                    }}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent>
              <div className="empty-state">
                <GitPullRequest className="empty-state-icon" />
                <p className="text-zinc-400">No change requests found</p>
                <p className="text-zinc-500 text-sm">
                  Submit a change request to modify existing documents
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* View Change Request Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Change Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Badge className={statusColors[selectedRequest.status]}>
                  {selectedRequest.status.replace('_', ' ')}
                </Badge>
                <span className="text-sm text-zinc-500 uppercase">
                  {selectedRequest.document_type}
                </span>
              </div>

              {selectedRequest.section && (
                <div>
                  <Label className="text-zinc-500 text-xs">Section</Label>
                  <p className="text-zinc-200">{selectedRequest.section}</p>
                </div>
              )}

              <div>
                <Label className="text-zinc-500 text-xs">Requested Change</Label>
                <p className="text-zinc-200 mt-1">{selectedRequest.requested_change}</p>
              </div>

              {/* AI Suggestion Section */}
              {selectedRequest.ai_suggestion && (
                <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-blue-400">AI Analysis</span>
                    {selectedRequest.ai_recommendation && (
                      <Badge className="ml-auto bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        {selectedRequest.ai_recommendation}
                      </Badge>
                    )}
                  </div>
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap">
                    {selectedRequest.ai_suggestion}
                  </p>
                </div>
              )}

              {/* Actions */}
              {canApprove && selectedRequest.status !== 'approved' && selectedRequest.status !== 'rejected' && (
                <div className="flex flex-wrap gap-3 pt-4 border-t border-zinc-800">
                  {selectedRequest.status === 'pending' && (
                    <Button
                      onClick={() => handleAIReview(selectedRequest.id)}
                      disabled={aiReviewLoading}
                      className="bg-blue-600 hover:bg-blue-500"
                      data-testid="ai-review-btn"
                    >
                      {aiReviewLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Analyzing...
                        </span>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Get AI Review
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={() => handleApprove(selectedRequest.id)}
                    className="bg-emerald-600 hover:bg-emerald-500"
                    data-testid="approve-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleReject(selectedRequest.id)}
                    variant="outline"
                    className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
                    data-testid="reject-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
